
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/components/auth/AuthProvider";
import { useEnrolledCourses } from "@/hooks/useEnrolledCourses";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { MyCourses } from "@/components/dashboard/MyCourses";
import { ProfileSection } from "@/components/dashboard/ProfileSection";
import { useTranslation } from "react-i18next";
import { toast } from "@/components/ui/use-toast";

export default function Dashboard({ lang = "en" }: { lang?: string }) {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { enrolledCourses, loading, refetch } = useEnrolledCourses({ 
    userId: user?.id, 
    authLoading 
  });
  
  // Improved auth handling
  useEffect(() => {
    if (!authLoading && !user) {
      // Only redirect if we've finished loading auth and user is not authenticated
      navigate("/auth");
    }
  }, [authLoading, user, navigate]);

  // Handle purchase success parameter
  useEffect(() => {
    const purchaseSuccess = searchParams.get('purchase_success');
    if (purchaseSuccess === 'true' && user && !authLoading) {
      // Show success message
      toast({
        title: "Payment Successful!",
        description: "Your course access has been granted. Your enrollment should appear below.",
      });
      
      // Force refetch enrolled courses after a short delay to allow webhook processing
      setTimeout(() => {
        refetch();
      }, 2000);
      
      // Clear the URL parameter to prevent repeated messages on refresh
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, user, authLoading, refetch]);

  if (authLoading) {
    return (
      <div className="container flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Safe guard to prevent flash of unauthenticated content
  if (!user) return null;

  return (
    <div className="container py-12">
      <DashboardHeader />
      
      <div className="space-y-8">
        {/* Add Profile Section */}
        <ProfileSection />
        
        {/* My Courses Section */}
        <MyCourses courses={enrolledCourses} loading={loading} lang={lang} />
      </div>
    </div>
  );
}
